import { useState, useRef, useEffect, MutableRefObject } from 'react'
import { Props_SnippetAccordion } from './Accordion.props'
import { Legacy_CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const CATEGORY_TRANSITION_MS = 300

const SnippetAccordion = ({heading, repeatable_category = [], repeatable_accordion = []}: Props_SnippetAccordion) => {
    const [activeCategory, setActiveCategory] = useState<number>(0)
    const [pendingCategory, setPendingCategory] = useState<number | null>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)
    const detailsRefs = useRef<Array<MutableRefObject<HTMLDetailsElement | null>>>([])

    useEffect(() => {
        detailsRefs.current = repeatable_accordion.map(
            (_, i) => detailsRefs.current[i] || { current: null }
        )
    }, [repeatable_accordion])

    useEffect(() => {
        if (pendingCategory !== null && pendingCategory !== activeCategory) {
            setActiveCategory(pendingCategory)
            const timeout = setTimeout(() => {
                detailsRefs.current.forEach(ref => {
                    if (ref.current) {
                        ref.current.open = false
                    }
                })
                setPendingCategory(null)
            }, CATEGORY_TRANSITION_MS)
            return () => clearTimeout(timeout)
        }
    }, [pendingCategory])

    const handleCategoryClick = (categoryIndex: number) => {
        if (categoryIndex !== activeCategory) {
            setPendingCategory(categoryIndex)
        }
    }

    const classHeadingVisibility: string = heading.visibility?.value ? `snippet-accordion__heading--${heading.visibility.value}` : ''

    return (
        <section className="snippet-accordion">
            <div className="snippet-accordion__container">
                <h2
                    ref={headingRef}
                    id="snippet-accordion-heading"
                    className={`snippet-accordion__heading ${classHeadingVisibility}`}
                >
                    {heading.content.value}
                </h2>
                <div
                    role="tablist"
                    aria-labelledby="snippet-accordion-heading"
                    className="snippet-accordion__container-categories"
                >
                    {repeatable_category.map((category, categoryIndex) => (
                        <button
                            key={`snippet-accordion-category-${categoryIndex}`}
                            role="tab"
                            className="snippet-accordion__category"
                            aria-selected={activeCategory === categoryIndex}
                            aria-controls={`snippet-accordion-category-group-${categoryIndex}`}
                            onClick={() => handleCategoryClick(categoryIndex)}
                            type="button"
                        >
                            {category.label?.value}
                        </button>
                    ))}
                </div>
                <div className="snippet-accordion__container-accordions">
                    {repeatable_category.map((category, categoryIndex) => (
                        <div
                            key={`snippet-accordion-category-group-${categoryIndex}`}
                            id={`snippet-accordion-category-group-${categoryIndex}`}
                            role="tabpanel"
                            aria-hidden={activeCategory !== categoryIndex}
                            className="snippet-accordion__category-group"
                        >
                            <div className="snippet-accordion__category-group-inner">
                                {repeatable_accordion.filter(accordion => accordion.categoryId?.value === category.id?.value).map((accordion, accordionId) => {
                                    const globalAccordionIndex = repeatable_accordion.findIndex(
                                        a =>
                                            a.categoryId?.value === category.id?.value &&
                                            a.heading?.value === accordion.heading?.value
                                    )
                                    return (
                                        <details
                                            key={`snippet-accordion-accordion-${accordionId}`}
                                            className="snippet-accordion__accordion"
                                            ref={el => {
                                                if (detailsRefs.current[globalAccordionIndex]) {
                                                    detailsRefs.current[globalAccordionIndex].current = el
                                                }
                                            }}
                                        >
                                            <summary className="snippet-accordion__accordion-summary">
                                                <h3 className="snippet-accordion__accordion-heading">
                                                    {accordion.heading?.value}
                                                </h3>
                                            </summary>
                                            <div className="snippet-accordion__accordion-content-container">
                                                {accordion.content?.value && (
                                                    <div className="snippet-accordion__accordion-content">
                                                        {Legacy_CompileMarkdown(accordion.content.value)}
                                                    </div>
                                                )}
                                            </div>
                                        </details>
                                    )
                                })}
                            </div>
                            { category.src?.value && (
                                <img
                                    src={category.src?.value || ''}
                                    alt={category.alt?.value || ''}
                                    className="snippet-accordion__category-group-image"
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default SnippetAccordion
